import React from 'react';
import { Box, Typography } from '@mui/material';
import BreadcrumbsContainer from '../../containers/common/breadcurmbs/BreadcrumbsContainer';
import KpiReportTemplate from '../../components/kpimng/KpiReportTemplate';
import * as kpiApi from '../../lib/api/kpiReport';
import { calcProcessDefectSummary } from '../../lib/kpiMetrics';

// 품질 불량률은 두 지표로 나뉜다 - 계산 단위가 달라 하나로 통일하지 않는다.
//   · 공정 불량률   : Σ공정불량수량 ÷ Σ공정생산수량 × 100 (검사 실시 QC_YN='Y' 공정만) - 수량 기준
//   · 완제품 불량률 : FAIL 판정 건수 ÷ 완품검사 판정 건수 × 100                        - 건수 기준
// 메인 대시보드(MainDashboardPage)와 같은 API(kpiApi.getProcessDefectKpi / getQualityKpi)·같은
// 필드를 그대로 써서 화면마다 숫자가 갈리지 않게 한다.

const fmt = (v, d = 2) => (v != null ? Number(v).toFixed(d) : '-');

// KpiDashboardPage(통합 대시보드) 의 품질 탭이 이 두 블록의 chartConfig/columns/summaryFn 을
// 그대로 import 해서 쓴다 - 같은 컬럼·같은 계산 코드를 재사용해 화면마다 값이 갈리지 않게 한다.

// ── 블록 1) 공정 불량률 (수량 기준, MANU_PRODUCT_ROUTING_DET.QC_YN='Y') ──────────────
// GET /kpi/process-defect(kpiApi.getProcessDefectKpi) 가 이 지표의 정본이다 - 계획 상태
// 제한이 없어(selectProductionKpi 와 달리) 진행중·QC_FAILED 계획의 공정불량도 포함한다.
// processOutputQty/processDefectQty/processDefectRate/goodRate 를 그대로 쓴다 -
// MainDashboardPage 와 동일한 API·필드.
export const processChartConfig = {
  type: 'line',
  bars: [
    { key: 'processDefectRate', name: '공정 불량률(%)', color: '#f44336' },
    { key: 'goodRate',          name: '양품률(%)',       color: '#388e3c' },
  ],
};

export const processColumns = [
  { field: 'kpiYm',     headerName: '년월',   width: 90 },
  { field: 'kpiYear',   headerName: '연도',   width: 70 },
  { field: 'productNm', headerName: '제품명', minWidth: 160, flex: 1 },
  {
    field: 'processOutputQty', headerName: '검사공정 생산수량', width: 140,
    description: '검사를 실시한 공정(QC_YN=Y)의 생산수량 합계 - 공정불량률의 분모',
    valueFormatter: (v) => fmt(v, 0),
  },
  {
    field: 'processDefectQty', headerName: '검사공정 불량수량', width: 140,
    description: '검사를 실시한 공정(QC_YN=Y)의 불량수량 합계 - 공정불량률의 분자',
    valueFormatter: (v) => fmt(v, 0),
  },
  {
    field: 'processDefectRate', headerName: '공정 불량률(%) · 수량기준', width: 170,
    description: 'Σ공정불량수량 ÷ Σ공정생산수량 × 100 (검사 실시 공정만)',
    valueFormatter: (v) => v != null ? `${fmt(v)}%` : '-',
  },
  {
    field: 'goodRate', headerName: '양품률(%)', width: 100,
    description: '100 − 공정 불량률. 공정불량률과 같은 모집단이라 두 값의 합은 항상 100이다',
    valueFormatter: (v) => v != null ? `${fmt(v, 1)}%` : '-',
  },
];

export const processSummaryFn = (list) => {
  const { totalOutput, totalDefect, defectRate, goodRate } = calcProcessDefectSummary(list);
  return [
    { label: '검사공정 생산수량', value: totalOutput.toLocaleString(), unit: '개', color: '#1976d2' },
    { label: '검사공정 불량수량', value: totalDefect.toLocaleString(), unit: '개', color: '#f44336' },
    { label: '공정 불량률(수량기준)', value: defectRate, unit: '%', color: '#f44336' },
    { label: '양품률',              value: goodRate, unit: '%', color: '#388e3c' },
  ];
};

// ── 블록 2) 완제품 불량률 (건수 기준, MANU_QC_INSPECTION INSPECTION_TYPE='FINAL') ────
// finalInspectionCnt/finalFailCnt/finalDefectRate 가 신규 정의(건수 기준)다. 기존
// inspectedQty/passQty/failQty/defectRate/qualityRate/ppm 은 완품검사 기준이지만 수량 기준으로
// 재는 레거시 필드라 그대로 두되 "레거시·수량기준" 임을 헤더에 밝힌다.
export const qualityChartConfig = {
  type: 'line',
  bars: [
    { key: 'finalDefectRate', name: '완제품 불량률(%)·건수', color: '#f44336' },
  ],
};

export const qualityColumns = [
  { field: 'kpiYm',        headerName: '년월',    width: 90 },
  { field: 'kpiYear',      headerName: '연도',    width: 70 },
  { field: 'productNm',    headerName: '제품명',  minWidth: 160, flex: 1 },
  {
    field: 'finalInspectionCnt', headerName: '완품검사 판정건수', width: 140,
    description: 'INSPECTION_TYPE=FINAL, STATUS IN (PASS,FAIL) 건수',
  },
  {
    field: 'finalFailCnt', headerName: '불합격(FAIL) 건수', width: 130,
  },
  {
    field: 'finalDefectRate', headerName: '완제품 불량률(%) · 건수기준', width: 190,
    description: 'FAIL 판정 건수 ÷ 완품검사 판정 건수 × 100. 완품 QC는 계획 전체를 한 번에 판정해 부분불량이 없어 건수로 잰다',
    valueFormatter: (v) => v != null ? `${fmt(v)}%` : '-',
  },
  {
    field: 'inspectedQty', headerName: '검사수량(레거시·수량)', width: 150,
    description: '완품검사 기준이지만 수량으로 잰 레거시 필드', valueFormatter: (v) => fmt(v, 0),
  },
  {
    field: 'passQty', headerName: '합격수량(레거시·수량)', width: 150,
    valueFormatter: (v) => fmt(v, 0),
  },
  {
    field: 'failQty', headerName: '불합격수량(레거시·수량)', width: 160,
    valueFormatter: (v) => fmt(v, 0),
  },
  {
    field: 'defectRate', headerName: '불량률(%)(레거시·수량)', width: 160,
    valueFormatter: (v) => v != null ? `${fmt(v)}%` : '-',
  },
  {
    field: 'qualityRate', headerName: '품질률(%)(레거시·수량)', width: 160,
    valueFormatter: (v) => v != null ? `${fmt(v)}%` : '-',
  },
  {
    field: 'ppm', headerName: 'PPM(레거시·수량)', width: 130,
    valueFormatter: (v) => v != null ? Number(v).toLocaleString() : '-',
  },
];

export const qualitySummaryFn = (list) => {
  const totalFinalInsp = list.reduce((s, r) => s + (Number(r.finalInspectionCnt) || 0), 0);
  const totalFinalFail = list.reduce((s, r) => s + (Number(r.finalFailCnt)       || 0), 0);
  const finalDefectRate = totalFinalInsp > 0 ? (totalFinalFail / totalFinalInsp * 100).toFixed(2) : '-';

  const totalInspected = list.reduce((s, r) => s + (Number(r.inspectedQty) || 0), 0);
  const totalPass      = list.reduce((s, r) => s + (Number(r.passQty)      || 0), 0);
  const totalFail       = list.reduce((s, r) => s + (Number(r.failQty)      || 0), 0);
  const qualityRate = totalInspected > 0 ? (totalPass / totalInspected * 100).toFixed(2) : '-';
  const ppm = totalInspected > 0 ? Math.round(totalFail / totalInspected * 1000000).toLocaleString() : '-';

  return [
    { label: '완제품 불량률(건수기준)', value: finalDefectRate, unit: '%', color: '#f44336' },
    { label: '완품검사 판정건수',       value: totalFinalInsp.toLocaleString(), unit: '건', color: '#1976d2' },
    { label: '불합격(FAIL) 건수',       value: totalFinalFail.toLocaleString(), unit: '건', color: '#f44336' },
    { label: '검사수량(레거시)',        value: totalInspected.toLocaleString(), unit: '개', color: '#9e9e9e' },
    { label: '품질률(레거시)',          value: qualityRate,                     unit: '%',  color: '#388e3c' },
    { label: 'PPM(레거시)',            value: ppm,                             unit: '',   color: '#7b1fa2' },
  ];
};

const KpiQualityPage = () => (
  <Box sx={{ p: 3 }}>
    <BreadcrumbsContainer title="목표관리/KPI 품질 리포트" icon="goal" />
    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
      공정 불량률(수량 기준)과 완제품 불량률(건수 기준)은 계산 단위가 달라 서로 직접 비교할 수
      없습니다 - 의도된 것입니다.
    </Typography>

    <Box sx={{ mb: 5 }}>
      <KpiReportTemplate
        title="공정 불량률"
        subtitle="※ 검사를 실시한 공정(QC_YN='Y')만 모집단인 수량 기준 지표입니다. 계획 상태 제한이 없어 진행중·QC_FAILED 계획도 포함합니다(GET /kpi/process-defect)."
        fetchFn={kpiApi.getProcessDefectKpi}
        chartConfig={processChartConfig}
        columns={processColumns}
        summaryFn={processSummaryFn}
      />
    </Box>

    <Box>
      <KpiReportTemplate
        title="완제품 불량률 (완품검사 기준)"
        subtitle="※ 완제품 불량률은 완품검사 판정 건수 기준입니다(부분 불량 없음). 나머지 검사수량·품질률·PPM 컬럼은 완품검사를 수량으로 재는 레거시 지표입니다."
        fetchFn={kpiApi.getQualityKpi}
        chartConfig={qualityChartConfig}
        columns={qualityColumns}
        summaryFn={qualitySummaryFn}
      />
    </Box>
  </Box>
);

export default KpiQualityPage;
